class KingpinMegaMenu {
  constructor(header) {
    this.header = header;
    this.section = header.closest('.shopify-section--header');
    this.triggers = [...header.querySelectorAll('[data-kp-pillar]')];
    this.panels = [...header.querySelectorAll('[data-kp-panel]')];
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.subcategoryHoverTimer = null;
    this.activeTrigger = null;
    this.hoverTimer = null;
    this.requestSequence = 0;
    this.mobileToggle = header.querySelector('[data-kp-mobile-toggle]');
    this.mobileDrawer = header.querySelector('[data-kp-mobile-drawer]');
    this.backdrop = header.querySelector('[data-kp-backdrop]');
    this.mobileBack = header.querySelector('[data-kp-mobile-back]');
    this.mobileTitle = header.querySelector('[data-kp-mobile-title]');
    this.mobileIcon = header.querySelector('[data-kp-mobile-icon]');

    this.enableSearch();
    this.bindDesktop();
    this.bindMobile();
    this.bindMobileSearch();
    this.bindSticky();
    this.rotateAnnouncements();
  }

  enableSearch() {
    this.header.querySelectorAll('.kp-header__search [tabindex="-1"]').forEach((element) => {
      element.removeAttribute('tabindex');
    });
  }

  bindDesktop() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        if (trigger.getAttribute('aria-expanded') === 'true') {
          this.closeDesktop(true);
        } else {
          this.openDesktop(trigger);
        }
      });

      trigger.addEventListener('mouseenter', () => {
        if (!window.matchMedia('(min-width: 1280px) and (pointer: fine)').matches) return;
        clearTimeout(this.hoverTimer);
        this.hoverTimer = window.setTimeout(() => this.openDesktop(trigger), 180);
      });
    });

    this.panels.forEach((panel) => {
      panel.querySelectorAll('[data-kp-subcategory]').forEach((button) => {
        button.addEventListener('click', () => this.selectSubcategory(panel, button));
        button.addEventListener('mouseenter', () => {
          if (!window.matchMedia('(min-width: 1280px) and (pointer: fine)').matches) return;
          clearTimeout(this.subcategoryHoverTimer);
          this.subcategoryHoverTimer = window.setTimeout(() => {
            if (!button.classList.contains('is-active')) this.selectSubcategory(panel, button);
          }, 60);
        });
      });
    });

    this.header.addEventListener('mouseleave', () => {
      if (!window.matchMedia('(min-width: 1280px) and (pointer: fine)').matches) return;
      clearTimeout(this.hoverTimer);
      this.hoverTimer = window.setTimeout(() => this.closeDesktop(false), 300);
    });

    this.header.addEventListener('mouseenter', () => clearTimeout(this.hoverTimer));

    document.addEventListener('click', (event) => {
      if (this.activeTrigger && !this.header.contains(event.target)) this.closeDesktop(false);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Tab' && this.mobileDrawer?.classList.contains('is-open')) {
        this.trapMobileFocus(event);
        return;
      }
      if (event.key !== 'Escape') return;
      if (this.header.classList.contains('is-search-open')) {
        this.closeMobileSearch();
      } else if (this.mobileDrawer?.classList.contains('is-open')) {
        this.closeMobile();
      } else if (this.activeTrigger) {
        this.closeDesktop(true);
      }
    });
  }

  openDesktop(trigger) {
    if (!window.matchMedia('(min-width: 1280px)').matches) return;

    const handle = trigger.dataset.kpPillar;
    const panel = this.header.querySelector(`[data-kp-panel="${CSS.escape(handle)}"]`);
    if (!panel) return;

    this.triggers.forEach((item) => item.setAttribute('aria-expanded', String(item === trigger)));
    this.panels.forEach((item) => {
      item.hidden = item !== panel;
    });

    this.activeTrigger = trigger;
    this.loadBanner(panel);

    const selected = panel.querySelector('[data-kp-subcategory].is-active')
      || panel.querySelector('[data-kp-subcategory]');
    if (selected) this.selectSubcategory(panel, selected);
    this.prewarmPanel(panel);
  }

  closeDesktop(returnFocus) {
    clearTimeout(this.hoverTimer);
    clearTimeout(this.subcategoryHoverTimer);
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    this.panels.forEach((panel) => {
      panel.hidden = true;
    });

    const previousTrigger = this.activeTrigger;
    this.activeTrigger = null;
    if (returnFocus && previousTrigger) previousTrigger.focus();
  }

  async selectSubcategory(panel, button) {
    const requestId = String(++this.requestSequence);
    panel.dataset.kpRequest = requestId;

    panel.querySelectorAll('[data-kp-subcategory]').forEach((item) => {
      const active = item === button;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', String(active));
    });

    const content = panel.querySelector('[data-kp-panel-content]');
    const url = button.dataset.panelUrl;
    if (!content || !url) return;

    const cached = this.cache.get(url);
    if (cached) {
      content.innerHTML = cached;
      content.removeAttribute('aria-busy');
      return;
    }

    content.setAttribute('aria-busy', 'true');
    content.innerHTML = '<div class="kp-mega-panel__loading">Loading menu…</div>';

    try {
      const markup = await this.fetchPanelMarkup(url);
      if (panel.dataset.kpRequest !== requestId || !button.classList.contains('is-active')) return;
      content.innerHTML = markup;
    } catch (error) {
      if (panel.dataset.kpRequest !== requestId || !button.classList.contains('is-active')) return;
      const collectionUrl = button.dataset.collectionUrl || '#';
      content.innerHTML = `
        <div class="kp-mega-detail">
          <p class="kp-mega-detail__empty">Shop-by links are not configured for this collection yet.</p>
          <a class="kp-mega-detail__view-all" href="${this.escapeAttribute(collectionUrl)}">View collection</a>
        </div>`;
      console.warn('[Kingpin mega menu]', error);
    } finally {
      if (panel.dataset.kpRequest === requestId) content.removeAttribute('aria-busy');
    }
  }

  fetchPanelMarkup(url) {
    if (this.cache.has(url)) return Promise.resolve(this.cache.get(url));
    if (this.pendingRequests.has(url)) return this.pendingRequests.get(url);

    const request = fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Panel request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const responsePanel = documentFragment.querySelector('[data-kp-panel-response]');
        if (!responsePanel) throw new Error('Panel response was empty');
        const markup = responsePanel.outerHTML;
        this.cache.set(url, markup);
        return markup;
      })
      .finally(() => this.pendingRequests.delete(url));

    this.pendingRequests.set(url, request);
    return request;
  }

  prewarmPanel(panel) {
    panel.querySelectorAll('[data-kp-subcategory][data-panel-url]').forEach((button) => {
      this.fetchPanelMarkup(button.dataset.panelUrl).catch((error) => {
        console.warn('[Kingpin mega menu prefetch]', error);
      });
    });
  }

  loadBanner(panel) {
    const image = panel.querySelector('[data-kp-banner-src]');
    if (!image || image.getAttribute('src')) return;
    image.setAttribute('src', image.dataset.kpBannerSrc);
    image.removeAttribute('data-kp-banner-src');
  }

  bindMobileSearch() {
    this.searchToggle = this.header.querySelector('[data-kp-search-toggle]');
    this.searchInput = this.header.querySelector('.search-menu__input');
    this.searchToggle?.addEventListener('click', () => {
      if (this.header.classList.contains('is-search-open')) {
        this.closeMobileSearch();
      } else {
        this.openMobileSearch();
      }
    });
    window.addEventListener('resize', () => {
      if (window.matchMedia('(min-width: 1280px)').matches) this.closeMobileSearch();
    });
  }

  openMobileSearch() {
    if (this.mobileDrawer?.classList.contains('is-open')) this.closeMobile();
    this.header.classList.add('is-search-open');
    this.searchToggle?.setAttribute('aria-expanded', 'true');
    window.setTimeout(() => this.searchInput?.focus(), 180);
  }

  closeMobileSearch() {
    this.header.classList.remove('is-search-open');
    this.searchToggle?.setAttribute('aria-expanded', 'false');
    this.searchInput?.blur();
  }

  bindMobile() {
    this.mobileToggle?.addEventListener('click', () => this.openMobile());
    this.header.querySelectorAll('[data-kp-mobile-close], [data-kp-backdrop]').forEach((button) => {
      button.addEventListener('click', () => this.closeMobile());
    });

    this.header.querySelectorAll('[data-kp-mobile-target]').forEach((control) => {
      control.addEventListener('click', (event) => {
        event.preventDefault();
        this.showMobileLevel(control.dataset.kpMobileTarget);
      });
    });

    this.mobileBack?.addEventListener('click', () => {
      const activePanel = this.mobileDrawer?.querySelector('[data-kp-mobile-level].is-active');
      this.showMobileLevel(activePanel?.dataset.mobileParent || '1');
    });

    window.addEventListener('resize', () => {
      if (window.matchMedia('(min-width: 1280px)').matches) {
        this.closeMobile();
      } else if (this.mobileDrawer?.classList.contains('is-open')) {
        this.positionMobileDrawer();
      }
    });
  }

  openMobile() {
    if (!this.mobileDrawer || !this.backdrop) return;
    this.closeMobileSearch();
    this.showMobileLevel('1');
    this.positionMobileDrawer();
    this.mobileDrawer.hidden = false;
    this.backdrop.hidden = false;
    requestAnimationFrame(() => this.mobileDrawer.classList.add('is-open'));
    this.mobileToggle?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('kp-menu-open');
    this.mobileDrawer.querySelector('[data-kp-mobile-close]')?.focus();
  }

  closeMobile() {
    if (!this.mobileDrawer || !this.backdrop) return;
    this.mobileDrawer.classList.remove('is-open');
    this.mobileToggle?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('kp-menu-open');
    this.backdrop.hidden = true;
    window.setTimeout(() => {
      if (!this.mobileDrawer.classList.contains('is-open')) this.mobileDrawer.hidden = true;
    }, 220);
    this.mobileToggle?.focus();
  }

  trapMobileFocus(event) {
    const focusable = [...this.mobileDrawer.querySelectorAll('button:not([disabled]), a[href]')]
      .filter((element) => !element.hidden && element.getClientRects().length > 0);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !this.mobileDrawer.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !this.mobileDrawer.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  }

  positionMobileDrawer() {
    if (!this.mobileDrawer) return;
    const utility = this.header.querySelector('.kp-header__utility');
    const top = Math.max(0, Math.round(utility?.getBoundingClientRect().bottom || 0));
    this.header.style.setProperty('--kp-mobile-drawer-top', `${top}px`);
  }

  showMobileLevel(level) {
    let activePanel = null;
    this.header.querySelectorAll('[data-kp-mobile-level]').forEach((panel) => {
      const active = panel.dataset.kpMobileLevel === level;
      panel.classList.toggle('is-active', active);
      if (active) activePanel = panel;
    });
    if (!activePanel) return;

    const depth = activePanel.dataset.mobileDepth || '1';
    this.mobileDrawer.dataset.mobileDepth = depth;
    this.mobileDrawer.scrollTop = 0;
    if (this.mobileTitle) this.mobileTitle.textContent = activePanel.dataset.mobileTitle || 'Shop';
    if (this.mobileBack) this.mobileBack.hidden = depth === '1';

    const iconHandle = activePanel.dataset.mobileIcon;
    const sourceIcon = depth === '1' || !iconHandle
      ? null
      : this.header.querySelector(`[data-kp-mobile-pillar="${CSS.escape(iconHandle)}"] .kp-pillar-icon`);
    if (this.mobileIcon) {
      this.mobileIcon.replaceChildren();
      if (sourceIcon) this.mobileIcon.append(sourceIcon.cloneNode(true));
      this.mobileIcon.hidden = !sourceIcon;
    }
  }

  bindSticky() {
    if (!this.section) return;
    const update = () => {
      const announcementHeight = this.header.querySelector('.kp-header__announcement')?.offsetHeight || 0;
      this.section.classList.toggle('is-kp-sticky', window.scrollY > announcementHeight);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  rotateAnnouncements() {
    const items = [...this.header.querySelectorAll('.kp-header__announcement-item')];
    if (items.length < 2) return;

    let active = 0;
    window.setInterval(() => {
      items[active].hidden = true;
      active = (active + 1) % items.length;
      items[active].hidden = false;
    }, 5000);
  }

  escapeAttribute(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('"', '&quot;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');
  }
}

const initKingpinMegaMenus = (root = document) => {
  root.querySelectorAll('[data-kp-header]:not([data-kp-initialized])').forEach((header) => {
    header.setAttribute('data-kp-initialized', '');
    new KingpinMegaMenu(header);
  });
};

document.addEventListener('DOMContentLoaded', () => initKingpinMegaMenus());
document.addEventListener('shopify:section:load', (event) => initKingpinMegaMenus(event.target));
